import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import { projectTypeLabel } from "@/lib/constants";
import AppShell from "@/components/AppShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { StatusChip } from "@/components/StatusChip";
import TailgatesTab from "@/components/TailgatesTab";
import JsaForm from "@/components/JsaForm";
import PhotosPanel from "@/components/PhotosPanel";
import FilesPanel from "@/components/FilesPanel";
import RecipientsCard from "@/components/RecipientsCard";
import SaveOfflineButton from "@/components/SaveOfflineButton";
import ShareLinkCard from "@/components/ShareLinkCard";
import Breadcrumb from "@/components/Breadcrumb";
import AuditForm from "@/components/AuditForm";
import PanoAuditForm from "@/components/pano/PanoAuditForm";
import SafetyOnlyBadge from "@/components/SafetyOnlyBadge";
import ReviewSummaryBadge, { ReviewSummaryRail } from "@/components/ReviewSummaryBadge";
import RelatedIncidentsSection from "@/components/RelatedIncidentsSection";
import EditProjectTypeButton from "@/components/EditProjectTypeButton";
import { CaptureMethodRow } from "@/components/pano/CaptureMethod";
import ProjectDocsPanel from "@/components/ProjectDocsPanel";
import NavigateButton from "@/components/NavigateButton";
import EditLocationButton from "@/components/EditLocationButton";
import { ChevronLeft, Download, Loader2, AlertCircle, CheckCircle2, FileCheck2, Archive, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const WORKFLOW = [
  "draft", "safety_complete", "field_complete", "testing_complete",
  "photo_complete", "ready_for_review", "approved", "closed",
];

function StepperRow({ status }) {
  const idx = WORKFLOW.indexOf(status);
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="workflow-stepper">
      {WORKFLOW.map((s, i) => {
        const active = i === idx;
        const done = i < idx || s === "approved" && status === "approved";
        return (
          <div key={s} className={`text-[10px] font-display font-bold tracking-wider uppercase px-2 py-1 rounded border ${active ? "border-blue-500 text-blue-300 bg-blue-950/40" : done ? "border-emerald-700 text-emerald-300 bg-emerald-950/30" : "border-slate-700 text-slate-500"}`}>
            {s.replace(/_/g, " ")}
          </div>
        );
      })}
    </div>
  );
}

export default function ProjectDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [tab, setTab] = useState("overview");
  const [closeoutCheck, setCloseoutCheck] = useState(null);
  const [genBusy, setGenBusy] = useState(false);
  // PDF photo layout choice — persisted in localStorage so a foreman's
  // preference survives a page refresh during their workflow.
  const [photoLayout, setPhotoLayout] = useState(
    () => localStorage.getItem("f2_photo_layout") || "thumbnails"
  );
  const choosePhotoLayout = (v) => {
    setPhotoLayout(v);
    localStorage.setItem("f2_photo_layout", v);
  };
  const [zipBusy, setZipBusy] = useState(false);
  const [genStatus, setGenStatus] = useState("");
  const [zipStatus, setZipStatus] = useState("");
  // Standalone Safety Package export — must declare these hooks BEFORE
  // the `if (!project)` early-return below so React's rules-of-hooks
  // checker is happy (hooks must run in the same order every render).
  const [safetyBusy, setSafetyBusy] = useState(false);
  const [safetyStatus, setSafetyStatus] = useState("");

  // Wrapped in useCallback so the effect/handlers below get stable
  // references and the exhaustive-deps lint stays happy. Both depend on
  // `id`, so they're recreated only when the route param changes.
  const refresh = useCallback(async () => {
    const { data } = await api.get(`/projects/${id}`);
    setProject(data);
  }, [id]);
  const refreshCloseout = useCallback(async () => {
    const { data } = await api.get(`/projects/${id}/closeout/check`);
    setCloseoutCheck(data);
  }, [id]);

  useEffect(() => { refresh(); refreshCloseout(); }, [refresh, refreshCloseout]);

  // Queued photos/JSA replayed in the background → reload so thumbnails appear.
  useEffect(() => {
    const onQueue = (e) => {
      if (navigator.onLine && e.detail?.replayed) { refresh().catch(() => {}); refreshCloseout(); }
    };
    window.addEventListener("f2:queue-changed", onQueue);
    return () => window.removeEventListener("f2:queue-changed", onQueue);
  }, [refresh, refreshCloseout]);

  if (!project) {
    return <AppShell><div className="text-slate-400">Loading…</div></AppShell>;
  }

  // Poll a closeout job every 2s until it completes or fails, calling
  // setProgress(msg) with the latest progress_msg from the backend.
  // Returns the final job descriptor. Throws on failure or timeout.
  //
  // Budget: 15 minutes. Large projects (500+ photos) can genuinely
  // take 5+ minutes to fetch every file from object storage, even
  // with 8-way parallel fetch. The old 5-min budget made the FRONTEND
  // give up while the backend was still working — user saw "Generation
  // timed out" but the artifact landed in storage a minute later with
  // nowhere to send the user. 15 min is well past the longest job we've
  // measured (Ratliff City ZIP at ~4 min).
  const pollJob = async (jobId, setProgress) => {
    const maxAttempts = 450; // 450 * 2s = 15 minutes
    for (let i = 0; i < maxAttempts; i++) {
      const { data: job } = await api.get(`/projects/${id}/closeout/jobs/${jobId}`);
      setProgress(`${job.progress_msg || job.status} · ${i * 2}s`);
      if (job.status === "completed") return job;
      if (job.status === "failed") throw new Error(job.error || "Generation failed");
      await new Promise((r) => setTimeout(r, 2000));
    }
    throw new Error("Generation timed out");
  };

  // Trigger a real browser download by hitting the artifact URL directly
  // (with the JWT as a query param) instead of fetching it into a Blob and
  // then synthesizing an anchor click. The blob approach has two failure
  // modes that bit us on big files:
  //   1) URL.revokeObjectURL() running on the same tick as a.click() can
  //      race the browser before it has read the blob — toast fires, file
  //      never lands.
  //   2) An anchor that isn't appended to document.body is silently ignored
  //      by Firefox.
  // Navigating to the streamed endpoint lets the browser handle the
  // download natively via Content-Disposition, with no in-memory copy.
  const triggerDownload = async (jobId) => {
    let url;
    try {
      const { signDownload } = await import("@/lib/signedUrl");
      url = await signDownload("closeout_job", jobId);
    } catch (_e) {
      // Fall back to a plain same-origin URL — the browser will attach
      // the httpOnly `f2_token` cookie automatically.
      url = `${API_BASE}/projects/${id}/closeout/jobs/${jobId}/download`;
    }
    const a = document.createElement("a");
    a.href = url;
    // Hint the browser this is a download, not a navigation. Server also
    // sends Content-Disposition: attachment for the same reason.
    a.rel = "noopener";
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    // Defer removal so the browser has time to start the download.
    setTimeout(() => {
      try { document.body.removeChild(a); } catch (_) { /* already gone */ }
    }, 2000);
  };

  // Extracts a human-readable error message from anything thrown by
  // the PDF/ZIP generation pipeline and shows it in a *persistent* toast
  // (no auto-dismiss). Field crews reported the previous transient toast
  // vanished before they could read it — critical info like a bad HEIC
  // file id or a storage backend hiccup was lost. Also mirrors the full
  // error into the browser console so it survives even if the user
  // accidentally dismisses the toast.
  const showGenerationError = (err, context, jobId) => {
    const detail = err?.response?.data?.detail;
    const msg = detail || err?.message || `${context} failed`;
    console.error(`[FieldOps] ${context} failed`, {
      job_id: jobId || null,
      status: err?.response?.status || null,
      detail,
      raw: err,
    });
    toast.error(msg, {
      duration: Infinity,
      closeButton: true,
      description: jobId ? `Job id: ${jobId}` : undefined,
      // Sonner-flavored id so retries don't stack duplicate toasts.
      id: `gen-error-${context}`,
    });
  };

  const generatePackage = async () => {
    setGenBusy(true);
    setGenStatus("queuing job…");
    let jobId = null;
    try {
      const { data: job } = await api.post(`/projects/${id}/closeout/generate`, {
        photo_layout: photoLayout,
      });
      jobId = job.id;
      const finished = await pollJob(job.id, setGenStatus);
      setGenStatus("downloading…");
      triggerDownload(finished.id);
      toast.success("Closeout package ready — download starting");
      refresh();
    } catch (err) {
      showGenerationError(err, "closeout PDF generation", jobId);
    } finally {
      setGenBusy(false);
      setGenStatus("");
    }
  };

  const downloadRawZip = async () => {
    setZipBusy(true);
    setZipStatus("queuing job…");
    let jobId = null;
    try {
      const { data: job } = await api.post(`/projects/${id}/raw/generate`);
      jobId = job.id;
      const finished = await pollJob(job.id, setZipStatus);
      setZipStatus("downloading…");
      triggerDownload(finished.id);
      toast.success("Raw files archive ready — download starting");
    } catch (err) {
      showGenerationError(err, "raw files ZIP", jobId);
    } finally {
      setZipBusy(false);
      setZipStatus("");
    }
  };

  const approveProject = async () => {
    try {
      await api.post(`/projects/${id}/approve`);
      toast.success("Project approved");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Approval failed");
    }
  };

  // Standalone Safety Package export — available on any project type.
  // Builds a PDF with just the cover + JSA + tailgates (no photos /
  // testing sections). Triggered from the Tailgate and JSA tabs so a
  // foreman can email customers a fresh PDF every day without dragging
  // the whole closeout pipeline along. State hooks are declared above
  // the `if (!project)` early-return; this is just the action handler.
  const exportSafetyPackage = async () => {
    setSafetyBusy(true);
    setSafetyStatus("queuing job…");
    let jobId = null;
    try {
      const { data: job } = await api.post(`/projects/${id}/safety/generate`);
      jobId = job.id;
      const finished = await pollJob(job.id, setSafetyStatus);
      setSafetyStatus("downloading…");
      await triggerDownload(finished.id);
      toast.success("Safety package ready — download starting");
    } catch (err) {
      showGenerationError(err, "safety package", jobId);
    } finally {
      setSafetyBusy(false);
      setSafetyStatus("");
    }
  };  const deleteProject = async () => {
    const confirmStr = window.prompt(
      `This will permanently delete the project ${project.site_number} — ${project.site_name}, all its safety forms, photos, screenshots and test docs.\n\nType DELETE to confirm:`,
      ""
    );
    if (confirmStr !== "DELETE") {
      if (confirmStr !== null) toast.error("Deletion cancelled — confirmation text did not match");
      return;
    }
    try {
      await api.delete(`/projects/${id}`);
      toast.success("Project deleted");
      navigate("/projects");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const canDelete = ["admin", "pm"].includes(user?.role);

  const canApprove = ["foreman", "pm", "admin"].includes(user?.role);

  // Human-readable labels for the tab segment of the breadcrumb. Kept in
  // a tiny map (not derived from JSX) so we don't have to read the DOM
  // to know the active tab's name.
  const TAB_LABELS = {
    overview: "Overview",
    tailgate: "Tailgate",
    jsa: "JSA",
    photos: "Photos",
    screenshots: "Screenshots",
    testdocs: "Test Docs",
    documents: "Documents",
    closeout: "Closeout",
  };

  return (
    <AppShell>
      <Breadcrumb
        items={[
          { label: "Projects", to: "/projects" },
          // Truncate very long site names to keep the crumb on one line.
          {
            label: (project.site_name || project.site_number || "Project").length > 28
              ? `${(project.site_name || project.site_number).slice(0, 27)}…`
              : (project.site_name || project.site_number || "Project"),
          },
          { label: TAB_LABELS[tab] || "Overview" },
        ]}
      />

      <Card className="bg-slate-900 border-slate-800 mb-5">
        <div className="brand-bar" />
        <CardContent className="p-5 sm:p-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-slate-400 font-display font-bold">Site</div>
              <div className="font-mono text-blue-400 text-sm mt-0.5">{project.site_number}</div>
              <h1 className="font-display font-black text-2xl sm:text-3xl tracking-tight mt-1" data-testid="project-title">{project.site_name}</h1>
              {/* Project name below the site — same site can host
                  multiple concurrent projects, so surface which one
                  this row/detail actually is. Skipped when the user
                  hasn't set a distinct project name. */}
              {project.project_name && project.project_name !== project.site_name && (
                <div
                  className="mt-1 text-sm font-display font-bold text-blue-300 truncate max-w-xl"
                  data-testid="project-name-subtitle"
                  title={project.project_name}
                >
                  {project.project_name}
                </div>
              )}
              <div className="text-sm text-slate-400 mt-1">
                {project.customer} · {projectTypeLabel(project.project_type)}
                {user?.role === "admin" && (
                  <EditProjectTypeButton project={project} onSaved={(p) => setProject(p)} />
                )}
              </div>
              {project.project_type === "pano_site_audit" && (
                <CaptureMethodRow project={project} canEdit={["admin", "pm"].includes(user?.role)} onSaved={refresh} />
              )}
              {/* Surface the actual photo templates applied so a user
                  can spot at-a-glance when a project has the wrong one
                  (e.g. an old "Generic" template instead of the
                  customer's COP template). Empty list shows a warning
                  pill prompting the PM to pick one. */}
              {project.applied_templates && project.applied_templates.length > 0 ? (
                <div
                  className="text-[11px] text-slate-500 mt-1.5 inline-flex flex-wrap items-center gap-1.5"
                  data-testid="applied-templates"
                >
                  <span className="uppercase tracking-wider font-display font-bold text-slate-400">Templates:</span>
                  {project.applied_templates.map((t) => (
                    <span
                      key={t.id}
                      className="px-1.5 py-0.5 rounded border border-slate-700 bg-slate-950 text-slate-200 font-mono"
                      data-testid={`applied-template-${t.id}`}
                    >
                      {t.name}
                    </span>
                  ))}
                </div>
              ) : project.project_type !== "safety_only" ? (
                <div
                  className="text-[11px] text-amber-300 mt-1.5 inline-flex items-center gap-1.5"
                  data-testid="applied-templates-empty"
                >
                  <span className="uppercase tracking-wider font-display font-bold">No photo template applied</span>
                  <span className="text-amber-200/70">— pick one from the Photos tab.</span>
                </div>
              ) : null}
            </div>
            <div className="text-right">
              <div className="flex items-center justify-end gap-2 flex-wrap">
                <SafetyOnlyBadge projectType={project.project_type} />
                <ReviewSummaryBadge
                  summary={project.review_summary}
                  variant="header"
                  onClick={() => setTab("photos")}
                />
                <StatusChip status={project.status} testid="project-status-chip" />
              </div>
              <div className="mt-2 flex items-center justify-end gap-2 flex-wrap">
                <ReviewSummaryRail
                  summary={project.review_summary}
                  onClick={() => setTab("photos")}
                />
                <NavigateButton project={project} />
                <SaveOfflineButton projectId={project.id} project={project} />
              </div>
              <div className="text-[10px] text-slate-500 mt-2 font-mono">Updated {new Date(project.updated_at).toLocaleString()}</div>
            </div>
          </div>
          <div className="mt-4">
            <StepperRow status={project.status} />
          </div>
        </CardContent>
      </Card>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="bg-slate-900 border border-slate-800 flex flex-wrap h-auto" data-testid="project-tabs">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="tailgate" data-testid="tab-tailgate">Tailgate</TabsTrigger>
          <TabsTrigger value="jsa" data-testid="tab-jsa">JSA</TabsTrigger>
          {project.project_type === "site_audit" && (
            <TabsTrigger value="audit" data-testid="tab-audit">Audit</TabsTrigger>
          )}
          {project.project_type === "pano_site_audit" && (
            <TabsTrigger value="pano-audit" data-testid="tab-pano-audit">Pano Audit</TabsTrigger>
          )}
          <TabsTrigger value="photos" data-testid="tab-photos">Photos</TabsTrigger>
          <TabsTrigger value="screenshots" data-testid="tab-screenshots">Screenshots</TabsTrigger>
          <TabsTrigger value="testdocs" data-testid="tab-testdocs">Test Docs</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
          <TabsTrigger value="closeout" data-testid="tab-closeout">Closeout</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4 space-y-4">
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-5 space-y-3">
              <KV label="Customer" v={project.customer} />
              <EditableProjectName
                project={project}
                canEdit={canDelete}
                onSaved={refresh}
              />
              <KV label="Market" v={project.market} />
              <LocationRow project={project} canEdit={canDelete} onSaved={refresh} />
              <KV label="Foreman" v={project.foreman} />
              <KV label="Date" v={project.project_date} />
              <KV label="Crew" v={(project.crew_members || []).map((c) => c.name).join(", ")} />
            </CardContent>
          </Card>

          {/* Related incidents — quiet "all clear" chip when none,
              red banner + list when there are events. Sits above the
              Danger Zone so safety context reads before destructive
              actions. */}
          <RelatedIncidentsSection projectId={project.id} />

          {canDelete && (
            <Card className="bg-slate-900 border-red-900/60 mt-4">
              <CardContent className="p-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="font-display font-bold text-red-300 flex items-center gap-2">
                      <Trash2 className="w-4 h-4" /> Danger Zone
                    </div>
                    <div className="text-xs text-slate-400 mt-1 max-w-md leading-snug">
                      Permanently delete this project, its safety forms, photos, screenshots, and test docs. This action cannot be undone.
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    onClick={deleteProject}
                    className="border-red-900 text-red-300 hover:bg-red-950/50 hover:text-red-200"
                    data-testid="delete-project-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-1.5" /> Delete Project
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="tailgate" className="mt-4 space-y-3">
          <ExportSafetyBar
            onClick={exportSafetyPackage}
            busy={safetyBusy}
            status={safetyStatus}
          />
          <TailgatesTab project={project} onChanged={() => { refresh(); refreshCloseout(); }} />
        </TabsContent>

        <TabsContent value="jsa" className="mt-4 space-y-3">
          <ExportSafetyBar
            onClick={exportSafetyPackage}
            busy={safetyBusy}
            status={safetyStatus}
          />
          <JsaForm project={project} initial={project.jsa} onSaved={(body, queued) => {
            // Offline save: unlock photos locally with the same rule the server uses.
            if (queued) {
              const hasLine = (body.items || []).some((it) => (it.activity || "").trim() || (it.hazards || []).some((h) => (h || "").trim()));
              const hasSig = (body.signatures || []).some((sg) => (sg.signature_data || "").trim());
              setProject((p) => ({ ...p, jsa: body, jsa_complete: hasLine && hasSig }));
              return;
            }
            refresh(); refreshCloseout();
          }} />
        </TabsContent>

        {project.project_type === "site_audit" && (
          <TabsContent value="audit" className="mt-4 space-y-3">
            <AuditForm project={project} onChanged={() => { refresh(); }} />
          </TabsContent>
        )}
        {project.project_type === "pano_site_audit" && (
          <TabsContent value="pano-audit" className="mt-4">
            <PanoAuditForm project={project} onSaved={refreshCloseout} />
          </TabsContent>
        )}

        <TabsContent value="photos" className="mt-4">
          <PhotosPanel project={project} onChanged={() => { refresh(); refreshCloseout(); }} onGoToJsa={() => setTab("jsa")} />
        </TabsContent>

        <TabsContent value="screenshots" className="mt-4">
          <FilesPanel project={project} kind="screenshot" onChanged={refresh} />
        </TabsContent>

        <TabsContent value="testdocs" className="mt-4">
          <FilesPanel project={project} kind="test_doc" onChanged={refresh} />
        </TabsContent>

        <TabsContent value="documents" className="mt-4">
          <ProjectDocsPanel project={project} onChanged={refresh} />
        </TabsContent>

        <TabsContent value="closeout" className="mt-4 space-y-4">
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-5 space-y-3">
              <div className="flex items-center justify-between">
                <div className="font-display font-bold text-lg">Closeout Readiness</div>
                {closeoutCheck?.ready ? (
                  <span className="inline-flex items-center gap-1.5 text-emerald-400"><CheckCircle2 className="w-4 h-4"/> Ready</span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-amber-400"><AlertCircle className="w-4 h-4"/> Blocked</span>
                )}
              </div>
              {closeoutCheck?.blockers?.length ? (
                <ul className="text-sm text-amber-300 list-disc list-inside space-y-1" data-testid="closeout-blockers">
                  {closeoutCheck.blockers.map((b) => <li key={b}>{b}</li>)}
                </ul>
              ) : <div className="text-sm text-emerald-400">All required items collected. You can generate the customer package.</div>}
            </CardContent>
          </Card>

          <div className="flex flex-wrap items-center gap-2">
            {/* Tiny layout toggle — sits inline with the generate button
                so the foreman can choose layout at the moment of trigger
                without diving into a settings page. */}
            <div
              className="inline-flex rounded-md border border-slate-700 bg-slate-950 p-0.5"
              role="radiogroup"
              aria-label="Photo layout"
              data-testid="photo-layout-toggle"
            >
              <button
                type="button"
                role="radio"
                aria-checked={photoLayout === "thumbnails"}
                onClick={() => choosePhotoLayout("thumbnails")}
                disabled={genBusy}
                title="3 photos per row, compact deliverable"
                data-testid="photo-layout-thumbnails"
                className={`px-2.5 py-1.5 text-[10px] font-display tracking-wider uppercase rounded-sm transition-colors ${
                  photoLayout === "thumbnails"
                    ? "bg-blue-700 text-white"
                    : "text-slate-400 hover:text-slate-200"
                }`}
              >
                Thumbnails
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={photoLayout === "full_page"}
                onClick={() => choosePhotoLayout("full_page")}
                disabled={genBusy}
                title="One photo per page for high-resolution audits"
                data-testid="photo-layout-fullpage"
                className={`px-2.5 py-1.5 text-[10px] font-display tracking-wider uppercase rounded-sm transition-colors ${
                  photoLayout === "full_page"
                    ? "bg-blue-700 text-white"
                    : "text-slate-400 hover:text-slate-200"
                }`}
              >
                Full Page
              </button>
            </div>
            <Button onClick={generatePackage} disabled={genBusy} className="bg-red-700 hover:bg-red-600 font-display tracking-wider" data-testid="generate-closeout">
              {genBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <Download className="w-4 h-4 mr-2"/>}
              GENERATE CLOSEOUT PDF
            </Button>
            <Button onClick={downloadRawZip} disabled={zipBusy} variant="outline" className="border-slate-700 bg-slate-900 hover:bg-slate-800 font-display tracking-wider" data-testid="download-raw-zip">
              {zipBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <Archive className="w-4 h-4 mr-2"/>}
              DOWNLOAD RAW FILES (ZIP)
            </Button>
            {canApprove && project.status === "ready_for_review" && (
              <Button onClick={approveProject} className="bg-emerald-700 hover:bg-emerald-600 font-display tracking-wider" data-testid="approve-project">
                <FileCheck2 className="w-4 h-4 mr-2"/> APPROVE PROJECT
              </Button>
            )}
            {(genBusy || zipBusy) && (
              <div className="text-xs text-slate-400 font-mono ml-1" data-testid="closeout-progress">
                {genBusy ? genStatus : zipStatus}
              </div>
            )}
          </div>

          <RecipientsCard
            projectId={project.id}
            canEdit={["foreman", "pm", "admin"].includes(user?.role)}
          />

          {["foreman", "pm", "admin"].includes(user?.role) && (
            <ShareLinkCard project={project} />
          )}
        </TabsContent>
      </Tabs>
    </AppShell>
  );
}

function ExportSafetyBar({ onClick, busy, status }) {
  return (
    <div
      className="flex flex-wrap items-center justify-between gap-3 p-3 rounded-md border border-amber-900/60 bg-amber-950/30"
      data-testid="export-safety-bar"
    >
      <div className="flex items-start gap-2 min-w-0">
        <FileCheck2 className="w-4 h-4 text-amber-300 mt-0.5 shrink-0" />
        <div className="min-w-0">
          <div className="font-display font-bold tracking-tight text-amber-100 text-sm">Daily Safety Package</div>
          <div className="text-[11px] text-amber-200/70">
            Builds a standalone PDF — JSA + every Tailgate to date — for customers that require a daily safety update.
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {busy && (
          <span className="text-[11px] text-amber-200/80 font-mono" data-testid="safety-export-status">{status}</span>
        )}
        <Button
          onClick={onClick}
          disabled={busy}
          variant="outline"
          className="border-amber-700 bg-amber-950/40 hover:bg-amber-950/70 text-amber-100 font-display tracking-wider"
          data-testid="export-safety-pdf"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1.5" />}
          EXPORT SAFETY PDF
        </Button>
      </div>
    </div>
  );
}

function KV({ label, v }) {
  return (
    <div className="grid grid-cols-3 gap-3 border-b border-slate-800 pb-2.5">
      <div className="text-xs uppercase tracking-wide text-slate-400 font-display font-bold">{label}</div>
      <div className="col-span-2 text-sm text-slate-100 font-mono">{v || "—"}</div>
    </div>
  );
}

/**
 * Location row that shows whichever the office actually filled in:
 * the street address, the GPS coordinates, or both. Coordinates
 * render as a small green pill so they're visually distinct from a
 * postal address, and if both are set the address takes the top line
 * (the human-readable value) with coords underneath.
 *
 * The coord line is tappable — clicking copies "lat,lng" to the
 * clipboard so a crew member on the road can paste it straight into
 * Waze, Apple Maps, or any other nav app they prefer. The green
 * Navigate button (Google Maps) is still the one-tap default, but
 * this gives crews an escape hatch when they have a favorite app.
 */
function LocationRow({ project, canEdit, onSaved }) {
  const address = (project?.address || "").trim();
  const lat = project?.latitude;
  const lng = project?.longitude;
  const hasCoords = typeof lat === "number" && typeof lng === "number";

  const copyCoords = async () => {
    if (!hasCoords) return;
    const text = `${lat},${lng}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`Copied ${text} — paste into any nav app.`);
    } catch {
      // Older Safari on iOS blocks clipboard writes outside of a
      // trusted gesture; fall back to a plain textarea copy hack.
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); toast.success(`Copied ${text}`); }
      catch { toast.error("Could not copy — long-press to copy manually."); }
      document.body.removeChild(ta);
    }
  };

  return (
    <div className="grid grid-cols-3 gap-3 border-b border-slate-800 pb-2.5">
      <div className="text-xs uppercase tracking-wide text-slate-400 font-display font-bold flex items-center gap-2">
        <span>Location</span>
        <EditLocationButton project={project} canEdit={canEdit} onSaved={onSaved} />
      </div>
      <div className="col-span-2 text-sm text-slate-100 font-mono space-y-1">
        {address && <div data-testid="overview-address">{address}</div>}
        {hasCoords && (
          <button
            type="button"
            onClick={copyCoords}
            className="flex items-center gap-2 group hover:bg-slate-800/50 rounded px-1 -mx-1 py-0.5 transition-colors"
            title="Tap to copy coordinates (paste into Waze, Apple Maps, etc.)"
            data-testid="overview-latlng"
          >
            <span className="inline-block text-[10px] font-display font-bold tracking-widest uppercase px-1.5 py-0.5 rounded bg-emerald-950/60 border border-emerald-800 text-emerald-300">
              GPS
            </span>
            <span>{lat.toFixed(6)}, {lng.toFixed(6)}</span>
            <span className="text-[10px] text-slate-500 group-hover:text-blue-300 transition-colors ml-1">
              tap to copy
            </span>
          </button>
        )}
        {!address && !hasCoords && (
          <span className="text-slate-500" data-testid="overview-location-empty">
            No location set{canEdit ? " — click 'edit' to add" : ""}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Inline-editable project name row. Renders as a normal KV for
 * foremen/technicians; admins and PMs see a "Rename" button that swaps
 * the value into an inline text input with Save / Cancel. Submitting
 * `PATCH /projects/{id}` updates the name; the parent refresh hook
 * pulls in the new value across the page (header, breadcrumbs, etc.).
 */
function EditableProjectName({ project, canEdit, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(project.project_name || "");
  const [busy, setBusy] = useState(false);

  // Keep the draft in sync if the project object reloads (e.g. after a
  // separate edit elsewhere). Doing this in `start()` instead of a
  // useEffect keeps the input untouched while actively editing.
  const start = () => {
    setDraft(project.project_name || "");
    setEditing(true);
  };

  const cancel = () => {
    setEditing(false);
    setDraft(project.project_name || "");
  };

  const save = async () => {
    const trimmed = draft.trim();
    if (!trimmed) {
      toast.error("Project name can't be empty.");
      return;
    }
    if (trimmed === (project.project_name || "")) {
      setEditing(false);
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/projects/${project.id}`, { project_name: trimmed });
      toast.success("Project name updated");
      setEditing(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update name");
    } finally {
      setBusy(false);
    }
  };

  if (!editing) {
    return (
      <div className="grid grid-cols-3 gap-3 border-b border-slate-800 pb-2.5">
        <div className="text-xs uppercase tracking-wide text-slate-400 font-display font-bold">Project Name</div>
        <div className="col-span-2 flex items-center justify-between gap-2">
          <div className="text-sm text-slate-100 font-mono" data-testid="project-name-display">
            {project.project_name || "—"}
          </div>
          {canEdit && (
            <button
              type="button"
              onClick={start}
              className="text-[10px] font-display font-bold tracking-wider uppercase text-blue-300 hover:text-blue-100 px-2 py-1 rounded border border-slate-700 hover:border-blue-700 transition-colors"
              data-testid="project-name-rename"
            >
              Rename
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-3 gap-3 border-b border-slate-800 pb-2.5">
      <div className="text-xs uppercase tracking-wide text-slate-400 font-display font-bold">Project Name</div>
      <div className="col-span-2 flex items-center gap-2">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") save();
            else if (e.key === "Escape") cancel();
          }}
          // Focus on enter — the row only enters edit mode on user
          // gesture, so jumping the caret into the input is the
          // natural next step (no a11y concern since this isn't a
          // page-load autofocus).
          autoFocus
          disabled={busy}
          className="flex-1 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-sm font-mono text-slate-100 focus:border-blue-500 focus:outline-none disabled:opacity-60"
          data-testid="project-name-input"
        />
        <button
          type="button"
          onClick={save}
          disabled={busy}
          className="text-[10px] font-display font-bold tracking-wider uppercase text-emerald-300 hover:text-emerald-100 px-2 py-1 rounded border border-emerald-700 hover:bg-emerald-950/40 transition-colors disabled:opacity-50"
          data-testid="project-name-save"
        >
          {busy ? "…" : "Save"}
        </button>
        <button
          type="button"
          onClick={cancel}
          disabled={busy}
          className="text-[10px] font-display font-bold tracking-wider uppercase text-slate-400 hover:text-slate-100 px-2 py-1 rounded border border-slate-700 hover:bg-slate-800 transition-colors disabled:opacity-50"
          data-testid="project-name-cancel"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
